/**
 * Feature availability — the one place public copy may learn whether a
 * capability it describes actually works today.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The landing page, the use-case cards and the playbook each promised things
 * that do not work for a real customer yet: Instagram and Messenger DMs from
 * people who are not on the Meta app need App Review, and Shopify's cart and
 * checkout topics are switched off, so abandoned-cart flows never fire for a
 * Shopify store; WhatsApp onboarding is not live. Nothing tied the copy to the truth, so it drifted.
 *
 * HOW TO USE
 * ----------
 *   • A feature is `live` only with evidence: repo-relative test paths (or a
 *     docs/ page recording a live proof). featureAvailability.test.ts proves
 *     every path exists, so "live" cannot be claimed with nothing behind it.
 *   • Every public claim line (channel bullets, use-case bullets, playbook
 *     automations) maps to a feature in CLAIMS. The same test fails when a
 *     rendered claim key has no entry, so new copy must be classified here.
 *   • A claim whose feature is `coming_soon` renders <ComingSoonBadge />.
 *     When the blocker clears, flip the status and add the evidence — the
 *     badge disappears everywhere at once.
 */
import type { MessageKey } from '@/i18n';

export type FeatureStatus = 'live' | 'coming_soon';

export type FeatureEntry =
  | { status: 'live'; evidence: readonly string[] }
  | { status: 'coming_soon'; blocker: string };

export const FEATURES = {
  // ── Meta: App Review approved (owner, 2026-10-10 16:10) ──────────────────
  // Live = proven on production on 2026-10-10 (support workspace, production
  // SQL): Instagram comment → DM (3 in 48 h, last 13:14 UTC); Facebook comment
  // → DM (13:54); Instagram DM → Default Flow with buttons; Messenger DMs
  // (13:23, 13:27); WhatsApp inbound → Default Flow → buttons → tap (12:40);
  // Instagram + Facebook publishing (14 posts in 72 h, last 14:02). The live
  // record is the Availability table in docs/FEATURES.md.
  'instagram.commentToDm': {
    status: 'live',
    evidence: ['server/src/tests/services/commentAutomationW4.test.ts', 'server/src/tests/services/commentToDmLog.test.ts', 'docs/FEATURES.md'],
  },
  'facebook.commentToDm': {
    status: 'live',
    evidence: ['server/src/tests/services/facebookCommentHandler.test.ts', 'docs/FEATURES.md'],
  },
  'instagram.dmAutomation': {
    status: 'live',
    evidence: ['server/src/tests/services/instagramAutomationDm.test.ts', 'server/src/tests/services/defaultFlowV2.test.ts', 'docs/FEATURES.md'],
  },
  'meta.connectAndPublish': {
    status: 'live',
    evidence: ['server/src/tests/services/instagramPublisher.test.ts', 'docs/FEATURES.md'],
  },
  'messenger.dmAutomation': {
    status: 'live',
    evidence: ['server/src/tests/services/messengerDefaultMenu.test.ts', 'docs/FEATURES.md'],
  },
  'whatsapp.messaging': {
    status: 'live',
    evidence: ['server/src/tests/services/chatFlowRichSend.test.ts', 'server/src/tests/services/whatsappConnectNumberActivation.test.ts', 'docs/FEATURES.md'],
  },
  // Not among today's production proofs, so still Coming soon.
  'instagram.storyReplies': {
    status: 'coming_soon',
    blocker: 'Story-reply automation has no production proof yet (2026-10-10)',
  },
  'instagram.followGate': {
    status: 'coming_soon',
    blocker: 'The follow check in comment → DM has no production proof yet (2026-10-10)',
  },
  'whatsapp.templates': {
    status: 'coming_soon',
    blocker: 'WhatsApp approved message templates (sends outside the 24 h window) have no production proof yet',
  },
  'messaging.inChatBooking': {
    status: 'coming_soon',
    blocker: 'Booking, confirmations and reminders inside a chat (WhatsApp, Instagram, Messenger) have no production proof yet',
  },
  'messaging.humanHandoff': {
    status: 'coming_soon',
    blocker: 'Handing a chat to a human mid-flow has no production proof yet',
  },
  // A flow STEP that sends a WhatsApp/Instagram/Messenger message on its own
  // (not a reply inside a conversation) has no production proof.
  'flows.outboundChannelSteps': {
    status: 'coming_soon',
    blocker: 'Flow steps that send WhatsApp, Instagram or Messenger messages unprompted have no production proof yet',
  },
  // P21: one person across platforms (owner Tier 2 roadmap).
  'identity.crossPlatform': {
    status: 'coming_soon',
    blocker: 'Owner Tier 2 roadmap (P21): one-person identity across platforms',
  },
  'shopify.abandonedCart': {
    status: 'coming_soon',
    blocker: 'Shopify protected customer data: cart and checkout webhook topics are off',
  },
  'inbox.customerRecord': {
    status: 'live',
    evidence: ['server/src/tests/services/conversationCustomer.test.ts'],
  },
  'email.lifecycleFlows': {
    status: 'live',
    evidence: [
      'server/src/tests/services/welcomeEmailService.test.ts',
      'server/src/tests/funnels/winbackFunnelDispatch.test.ts',
      'server/src/tests/flows/reviewCrossSellEmailsAreMarketing.test.ts',
      'server/src/tests/flows/saasTrialOnboardingPack.test.ts',
      'server/src/tests/services/kindSeedFlows.test.ts',
    ],
  },
  'email.campaigns': {
    status: 'live',
    evidence: ['server/src/tests/routes/campaignSegmentTargeting.test.ts'],
  },
  'popups.forms': {
    status: 'live',
    evidence: ['server/src/tests/services/popupLeads.test.ts'],
  },
  'booking.reminders': {
    status: 'live',
    evidence: ['server/src/tests/services/bookingReminderGate.test.ts', 'server/src/tests/flows/bookingRebookCooldown.test.ts'],
  },
  'booking.callLink': {
    status: 'live',
    evidence: ['server/src/tests/services/callRoomService.test.ts'],
  },
  // Connect is live (shopify.connect). Store-sync claims on landing / Getting
  // Started stay Coming soon until a live QA PASS on catalog + order sync.
  'shopify.connect': {
    status: 'live',
    evidence: [
      'server/src/tests/routes/shopifyConnectIntent.routes.test.ts',
      'server/src/tests/services/shopifyConnectIntent.test.ts',
      'server/src/tests/services/shopifyOAuthConfig.test.ts',
      'client/src/pages/settings/IntegrationsMarketplace/ShopifyConnectModal.test.tsx',
      'client/src/lib/shopifyConnect.test.ts',
    ],
  },
  // Owner 2026-10-10 16:12: "approval for everything except Shopify". Connect
  // is now offered in-app; public store-sync claims stay Coming soon.
  'shopify.storeSync': {
    status: 'coming_soon',
    blocker: 'No live QA PASS on Shopify catalog/order sync for a public merchant',
  },
  'calendar.googleSync': {
    status: 'live',
    evidence: ['server/src/tests/services/googleCalendarOAuth.test.ts', 'server/src/tests/services/calendarSyncEngine.test.ts'],
  },
  'payments.stripeCardcom': {
    status: 'live',
    evidence: ['server/src/tests/services/stripeKeyService.test.ts', 'server/src/tests/services/cardcomCollectPayment.test.ts'],
  },
  // H5: read-only Business Hub connectors — Coming soon until live sandbox PASS.
  'payments.israelConnectors': {
    status: 'coming_soon',
    blocker: 'PayPlus, Grow, Tranzila and Cardcom connectors await the owner\'s live sandbox PASS (docs/OWNER-QUEUE.md)',
  },
  'payments.paddle': {
    status: 'coming_soon',
    blocker: 'Paddle connector awaits the owner\'s live sandbox PASS (docs/OWNER-QUEUE.md)',
  },
  'payments.lemonsqueezy': {
    status: 'coming_soon',
    blocker: 'Lemon Squeezy connector awaits the owner\'s live sandbox PASS (docs/OWNER-QUEUE.md)',
  },
  'payments.gumroad': {
    status: 'coming_soon',
    blocker: 'Gumroad connector awaits the owner\'s live sandbox PASS (docs/OWNER-QUEUE.md)',
  },
  'payments.square': {
    status: 'coming_soon',
    blocker: 'Square connector awaits the owner\'s live sandbox PASS (docs/OWNER-QUEUE.md)',
  },
  'payments.mollie': {
    status: 'coming_soon',
    blocker: 'Mollie connector awaits the owner\'s live sandbox PASS (docs/OWNER-QUEUE.md)',
  },
  'payments.razorpay': {
    status: 'coming_soon',
    blocker: 'Razorpay connector awaits the owner\'s live sandbox PASS (docs/OWNER-QUEUE.md)',
  },
  'payments.chargebee': {
    status: 'coming_soon',
    blocker: 'Chargebee connector awaits the owner\'s live sandbox PASS (docs/OWNER-QUEUE.md)',
  },
  'payments.recurly': {
    status: 'coming_soon',
    blocker: 'Recurly connector awaits the owner\'s live sandbox PASS (docs/OWNER-QUEUE.md)',
  },
  'email.sending': {
    status: 'live',
    evidence: ['server/src/tests/services/resendTransport.test.ts'],
  },
  'mcp.connector': {
    status: 'live',
    evidence: ['server/src/tests/mcp/callTools.test.ts'],
  },
  'social.tiktokConnect': {
    status: 'live',
    evidence: [
      'server/src/tests/tiktok/tiktokRoutes.test.ts',
      'server/src/tests/tiktok/tiktokConnector.test.ts',
    ],
  },
  'social.tiktokPublish': {
    status: 'coming_soon',
    blocker: 'No live publish PASS; until TikTok approves the audit, posts go to private accounts only',
  },
  // Connect + advertiser list is live. Campaign UI stays Coming soon (ads.adAccounts).
  'ads.tiktokBusiness': {
    status: 'live',
    evidence: [
      'server/src/tests/services/tiktokAdsOAuthConfig.test.ts',
      'server/src/tests/routes/tiktokAdsOAuthRoutes.test.ts',
      'client/src/pages/settings/TikTokAdsSettings.test.tsx',
    ],
  },
  'social.xConnect': {
    status: 'live',
    evidence: [
      'server/src/tests/routes/socialOAuthYouTubeX.test.ts',
      'server/src/tests/routes/socialOAuthXOAuth1.test.ts',
      'server/src/tests/services/xToken.test.ts',
    ],
  },
  'social.xPublish': {
    status: 'coming_soon',
    blocker: 'No live publish PASS on X',
  },
  'social.youtubeConnect': {
    status: 'live',
    evidence: [
      'server/src/tests/routes/youtubeOAuthUrlBuilding.test.ts',
      'server/src/tests/routes/socialOAuthYouTubeX.test.ts',
      'server/src/tests/services/youtubeToken.test.ts',
    ],
  },
  // Kept Coming soon by the owner's 2026-10-10 list (TikTok / X / YouTube).
  // Production holds 6 posts with a YouTube id (SocialPost.platformPostIds),
  // so this can flip to live with an owner GO. Connect is live separately.
  'social.youtubePublish': {
    status: 'coming_soon',
    blocker: 'Not in the owner\'s 2026-10-10 list of live-proven capabilities',
  },
  // Connect is live. AI auto-reply stays Coming soon (gmail.aiSupport).
  'gmail.connect': {
    status: 'live',
    evidence: [
      'server/src/tests/services/gmailOAuthConfig.test.ts',
      'server/src/tests/routes/gmailOAuthRoutes.test.ts',
      'server/src/tests/routes/webhooks/gmailPubsubToken.test.ts',
      'client/src/pages/settings/GmailSettings.test.tsx',
    ],
  },
  // Connect + listAccessibleCustomers is live. Campaign UI stays Coming soon (ads.adAccounts).
  'googleAds.connect': {
    status: 'live',
    evidence: [
      'server/src/tests/services/googleAdsRedirectUri.test.ts',
      'server/src/tests/routes/googleAdsOAuthRoutes.test.ts',
      'client/src/pages/settings/GoogleAdsSettings.test.tsx',
    ],
  },
  // ── Owner Tier 2 roadmap (2026-10-10): never offered as available ────────
  // P9: sending from the merchant's own domain (white-label sender domain).
  'email.ownDomain': {
    status: 'coming_soon',
    blocker: 'Owner Tier 2 roadmap (P9): white-label email under the merchant’s own domain',
  },
  // P17: chatbot questions and answers imported from a CSV file or a Sheet.
  'chatbot.csvImport': {
    status: 'coming_soon',
    blocker: 'Owner Tier 2 roadmap (P17): CSV/Sheet chatbot knowledge import',
  },
  // P19: expenses (and sending them to the accountant). The Finance tab's
  // Recurring Expenses view reads Coming soon until it ships.
  'finance.expenses': {
    status: 'coming_soon',
    blocker: 'Owner Tier 2 roadmap (P19): expenses to accountant',
  },
  // P27: CRM import from HubSpot, Pipedrive, Zoho, Salesforce, Monday, Sheets.
  'crm.import': {
    status: 'coming_soon',
    blocker: 'Owner Tier 2 roadmap (P27): CRM import from HubSpot/Pipedrive/Zoho/Salesforce/Monday/Sheets',
  },
  // P20: A/B testing with per-variant results (a flow split only routes people;
  // nothing reports a variant's results or picks a winner).
  'flows.abTesting': {
    status: 'coming_soon',
    blocker: 'Owner Tier 2 roadmap (P20): A/B testing and unified ads + email performance',
  },
  // ── Tier 1, no production proof today ───────────────────────────────────
  // Production read 2026-10-10: the only Google Ads connection is
  // needs_reauth and never synced; Meta ad accounts wait on App Review; TikTok
  // ads were never connected.
  'ads.adAccounts': {
    status: 'coming_soon',
    blocker: 'No live ad-account sync: Meta Ads awaits App Review, the one Google Ads connection never synced (needs_reauth)',
  },
  // Production read 2026-10-10: no WooCommerce, Wix or BigCommerce store has
  // ever connected. Shopify is live (shopify.storeSync).
  'store.otherPlatforms': {
    status: 'coming_soon',
    blocker: 'No production store has connected through WooCommerce, Wix or BigCommerce',
  },
  // "Auto-reply to customer emails using AI": BuzzBit X runs no AI of its own,
  // and automatic customer-facing replies need owner approval.
  'gmail.aiSupport': {
    status: 'coming_soon',
    blocker: 'No built-in AI (owner decision); automatic customer-facing replies need owner approval',
  },
  'community.telegram': {
    status: 'coming_soon',
    blocker: 'No production Telegram or community-link connection; not verified live',
  },
  // Live host passes (2026-09-29, #771): Claude Code, Codex, Cursor. ChatGPT
  // and local models have no live pass.
  'ai.chatgpt': {
    status: 'coming_soon',
    blocker: 'No live connector pass on ChatGPT yet',
  },
  'ai.localModels': {
    status: 'coming_soon',
    blocker: 'No live pass with a real local model (Ollama / LM Studio)',
  },
  // Reach, comments and DMs on the Hub come from a connected Instagram or
  // Facebook account (Meta App Review); per-post metrics are not on main.
  // FEATURES.md Availability today: mobile push notifications are not live.
  'push.mobile': {
    status: 'coming_soon',
    blocker: 'Mobile push notifications are not live',
  },
  'hub.socialMetrics': {
    status: 'coming_soon',
    blocker: 'Reach, comments and DMs need a connected Instagram/Facebook account (Meta App Review)',
  },
} as const satisfies Record<string, FeatureEntry>;

export type FeatureKey = keyof typeof FEATURES;

/** Every public claim line → the feature it promises. */
export const CLAIMS = {
  // Landing — Product menu descriptions
  'landing.nav.p.instagramDesc': 'instagram.commentToDm',
  'landing.nav.p.whatsappDesc': 'whatsapp.templates', // "Flows, templates, bookings and support": templates and bookings unproven
  'landing.nav.p.messengerDesc': 'messenger.dmAutomation',
  'landing.nav.p.emailDesc': 'email.lifecycleFlows',
  'landing.nav.p.bookingDesc': 'booking.reminders',
  // Landing — "Works with the tools you already use" strip
  'landing.integrations.instagram': 'meta.connectAndPublish',
  'landing.integrations.facebook': 'meta.connectAndPublish',
  'landing.integrations.whatsapp': 'whatsapp.messaging',
  'landing.integrations.shopify': 'shopify.storeSync',
  'landing.integrations.gcal': 'calendar.googleSync',
  'landing.integrations.payments': 'payments.stripeCardcom',
  'landing.integrations.resend': 'email.sending',
  'landing.integrations.mcp': 'mcp.connector',
  // Landing — channel tabs
  'landing.channels.instagram.1': 'instagram.commentToDm',
  'landing.channels.instagram.2': 'instagram.storyReplies',
  'landing.channels.instagram.3': 'instagram.dmAutomation',
  'landing.channels.whatsapp.1': 'whatsapp.templates', // buttons are live, approved templates are not
  'landing.channels.whatsapp.2': 'messaging.inChatBooking',
  'landing.channels.whatsapp.3': 'messaging.humanHandoff',
  'landing.channels.messenger.1': 'messenger.dmAutomation',
  'landing.channels.messenger.2': 'messaging.inChatBooking',
  // "Every conversation lands on the same customer record" reads as one
  // person across platforms: the owner's Tier 2 roadmap (P21).
  'landing.channels.messenger.3': 'identity.crossPlatform',
  'landing.channels.email.1': 'email.lifecycleFlows',
  'landing.channels.email.2': 'email.campaigns',
  'landing.channels.email.3': 'popups.forms',
  // Landing — use-case cards
  'landing.useCases.ecommerce.1': 'shopify.abandonedCart',
  'landing.useCases.ecommerce.2': 'instagram.commentToDm',
  'landing.useCases.ecommerce.3': 'email.lifecycleFlows',
  'landing.useCases.services.1': 'messaging.inChatBooking',
  'landing.useCases.services.2': 'booking.reminders',
  'landing.useCases.services.3': 'booking.reminders',
  'landing.useCases.coaching.1': 'instagram.followGate', // "…with a follow check"
  'landing.useCases.coaching.2': 'booking.callLink',
  'landing.useCases.coaching.3': 'email.lifecycleFlows',
  'landing.useCases.courses.1': 'email.lifecycleFlows',
  'landing.useCases.courses.2': 'instagram.commentToDm',
  'landing.useCases.courses.3': 'email.lifecycleFlows',
  'landing.useCases.saas.1': 'email.lifecycleFlows',
  'landing.useCases.saas.2': 'messaging.inChatBooking',
  'landing.useCases.saas.3': 'email.lifecycleFlows',
  'landing.useCases.creators.1': 'instagram.commentToDm',
  'landing.useCases.creators.2': 'instagram.storyReplies',
  'landing.useCases.creators.3': 'instagram.dmAutomation',
  // Playbook automations
  'landing.pb.ecommerce.1.name': 'instagram.commentToDm',
  'landing.pb.ecommerce.2.name': 'shopify.abandonedCart',
  'landing.pb.ecommerce.3.name': 'email.lifecycleFlows',
  'landing.pb.ecommerce.4.name': 'whatsapp.messaging',
  'landing.pb.services.1.name': 'messaging.inChatBooking',
  'landing.pb.services.2.name': 'booking.reminders',
  'landing.pb.services.3.name': 'booking.reminders',
  'landing.pb.services.4.name': 'instagram.dmAutomation',
  'landing.pb.coaching.1.name': 'instagram.commentToDm',
  'landing.pb.coaching.2.name': 'booking.callLink',
  'landing.pb.coaching.3.name': 'email.lifecycleFlows',
  'landing.pb.coaching.4.name': 'email.lifecycleFlows',
  'landing.pb.courses.1.name': 'popups.forms',
  'landing.pb.courses.2.name': 'instagram.commentToDm',
  'landing.pb.courses.3.name': 'email.lifecycleFlows',
  'landing.pb.saas.1.name': 'email.lifecycleFlows',
  'landing.pb.saas.2.name': 'messaging.inChatBooking',
  'landing.pb.saas.3.name': 'email.lifecycleFlows',
  'landing.pb.creators.1.name': 'instagram.commentToDm',
  'landing.pb.creators.2.name': 'instagram.storyReplies',
  'landing.pb.creators.3.name': 'instagram.dmAutomation',
  // Landing — "Watch the flows run" cards (a: Instagram, b/c: WhatsApp)
  'landing.flows.a.title': 'instagram.commentToDm',
  'landing.flows.b.title': 'messaging.inChatBooking',
  'landing.flows.c.title': 'messaging.humanHandoff',
  'landing.flows.d.title': 'email.lifecycleFlows',
  'landing.flows.e.title': 'hub.socialMetrics',
  // Pricing (Settings → Billing) — the messaging-channel line of each plan
  'billing.plans.feature.allChannels': 'whatsapp.messaging',
  'billing.plans.feature.oneChannel': 'instagram.dmAutomation',
} as const satisfies Partial<Record<MessageKey, FeatureKey>>;

/**
 * The channels the landing hero and the demo name, each with the feature that
 * makes it work for a customer who signs up today. A channel whose feature is
 * not live is shown with the Coming-soon badge.
 */
export const CHANNEL_FEATURES = [
  { label: 'landing.nav.p.email', feature: 'email.lifecycleFlows' },
  { label: 'landing.nav.p.booking', feature: 'booking.reminders' },
  { label: 'landing.nav.p.instagram', feature: 'instagram.dmAutomation' },
  { label: 'landing.nav.p.whatsapp', feature: 'whatsapp.messaging' },
  { label: 'landing.nav.p.messenger', feature: 'messenger.dmAutomation' },
] as const satisfies readonly { label: MessageKey; feature: FeatureKey }[];

/** The hero phone's channel tabs → the feature each scene shows. */
export const SCENE_CHANNEL_FEATURES = {
  instagram: 'instagram.commentToDm',
  whatsapp: 'whatsapp.messaging',
  messenger: 'messenger.dmAutomation',
  email: 'email.lifecycleFlows',
} as const satisfies Record<string, FeatureKey>;

/**
 * Integration tiles whose connect promise is a feature here. The registry's
 * `status: 'coming_soon'` must agree with the feature (featureAvailability
 * test), and the marketplace shows Coming soon instead of Connect for it.
 */
export const INTEGRATION_FEATURES = {
  tiktok_business: 'social.tiktokConnect',
  tiktok_ads: 'ads.tiktokBusiness',
  x_social: 'social.xConnect',
  email_sending: 'email.ownDomain',
  shopify: 'shopify.connect',
  youtube: 'social.youtubeConnect',
  gmail: 'gmail.connect',
  google_ads: 'googleAds.connect',
  woocommerce: 'store.otherPlatforms',
  wix: 'store.otherPlatforms',
  telegram: 'community.telegram',
  community_link: 'community.telegram',
  chatgpt: 'ai.chatgpt',
  local_llm: 'ai.localModels',
  // Listed by #1218 (claude/no-dead-picks) as coming_soon tiles; mapped here
  // so the two PRs agree whichever merges first.
  hubspot_crm: 'crm.import',
  salesforce_crm: 'crm.import',
  google_sheets: 'crm.import',
} as const satisfies Record<string, FeatureKey>;

export function isFeatureLive(feature: FeatureKey): boolean {
  return FEATURES[feature].status === 'live';
}

/** The feature a claim line promises, or undefined when the key is no claim. */
export function claimFeature(key: MessageKey): FeatureKey | undefined {
  return (CLAIMS as Partial<Record<MessageKey, FeatureKey>>)[key];
}

/**
 * Whether the claim a message key makes is backed by a live feature.
 * An unmapped key reads as not live — the safe default for public copy.
 */
export function isClaimLive(key: MessageKey): boolean {
  const feature = claimFeature(key);
  return feature !== undefined && isFeatureLive(feature);
}
